import type { IntelligenceSetupDraft } from './setup-draft';
import type { SetupTopic, SetupPrompt } from '@/lib/prompt-topics/contract';
export function draftRefs(draft: Pick<IntelligenceSetupDraft, 'prompts' | 'promptRefs'>): string[] {
  return draft.prompts.map((_, i) => draft.promptRefs?.[i] || `p${i}`);
}
export function appendTopicPrompts(
  draft: IntelligenceSetupDraft,
  prompts: string[],
  topics?: string[]
): IntelligenceSetupDraft {
  const next = {
    ...draft,
    prompts: [...draft.prompts],
    promptRefs: draftRefs(draft),
    promptGroups: (draft.promptGroups || []).map(g => ({
      ...g,
      prompt_refs: [...g.prompt_refs],
      ...(g.subtopics !== undefined
        ? {
            subtopics: g.subtopics.map(child => ({
              ...child,
              prompt_refs: [...child.prompt_refs],
            })),
          }
        : {}),
    })),
  };
  prompts.forEach((text, i) => {
    let index = topics ? next.prompts.indexOf(text) : -1;
    if (index < 0) {
      index = next.prompts.length;
      next.prompts.push(text);
      next.promptRefs.push(`p${crypto.randomUUID().replaceAll('-', '').slice(0, 24)}`);
    }
    const name = topics?.[i]?.trim();
    if (name) {
      let group = next.promptGroups.find(
        g => g.name.toLocaleLowerCase('en-US') === name.toLocaleLowerCase('en-US')
      );
      if (!group) {
        group = { name, prompt_refs: [] };
        next.promptGroups.push(group);
      }
      if (!group.prompt_refs.includes(next.promptRefs[index]))
        group.prompt_refs.push(next.promptRefs[index]);
    }
  });
  return next;
}
export function removeTopicPrompt(
  draft: IntelligenceSetupDraft,
  index: number
): IntelligenceSetupDraft {
  const refs = draftRefs(draft);
  const removed = refs[index];
  return {
    ...draft,
    prompts: draft.prompts.filter((_, i) => i !== index),
    promptRefs: refs.filter((_, i) => i !== index),
    promptGroups: draft.promptGroups?.map(g => ({
      ...g,
      prompt_refs: g.prompt_refs.filter(r => r !== removed),
      ...(g.subtopics !== undefined
        ? {
            subtopics: g.subtopics.map(child => ({
              ...child,
              prompt_refs: child.prompt_refs.filter(r => r !== removed),
            })),
          }
        : {}),
    })),
  };
}
export function reviseTopicPrompts(
  draft: IntelligenceSetupDraft,
  prompts: string[]
): Partial<IntelligenceSetupDraft> {
  const refs = draftRefs(draft);
  const used = new Set<number>();
  const promptRefs = prompts.map(text => {
    const i = draft.prompts.findIndex((p, i) => p === text && !used.has(i));
    if (i >= 0) {
      used.add(i);
      return refs[i];
    }
    return `p${crypto.randomUUID().replaceAll('-', '').slice(0, 24)}`;
  });
  return {
    prompts,
    promptRefs,
    promptGroups: draft.promptGroups?.map(g => ({
      ...g,
      prompt_refs: g.prompt_refs.filter(r => promptRefs.includes(r)),
      ...(g.subtopics !== undefined
        ? {
            subtopics: g.subtopics.map(child => ({
              ...child,
              prompt_refs: child.prompt_refs.filter(
                r => promptRefs.includes(r) && g.prompt_refs.includes(r)
              ),
            })),
          }
        : {}),
    })),
  };
}
export function restoreTopicSampler(
  prompts: SetupPrompt[],
  groups?: SetupTopic[]
): Partial<IntelligenceSetupDraft> {
  return {
    prompts: prompts.map(p => (typeof p === 'string' ? p : p.text)),
    promptRefs: prompts.map((p, i) => (typeof p === 'string' ? `p${i}` : p.ref)),
    promptGroups: groups || [],
  };
}

/** Keep stable editor IDs locally; remap to short references in the bounded request. */
export function encodeTopicDraft(
  draft: Pick<IntelligenceSetupDraft, 'prompts' | 'promptRefs' | 'promptGroups'>
): { prompts: SetupPrompt[]; prompt_groups?: SetupTopic[] } {
  const refs = draftRefs(draft);
  const rows = draft.prompts
    .map((text, i) => ({ text: text.trim(), original: refs[i], ref: `p${i.toString(36)}` }))
    .filter(p => p.text);
  const refMap = new Map(rows.map(p => [p.original, p.ref]));
  const groups = (draft.promptGroups || [])
    .map(g => ({
      name: g.name,
      prompt_refs: g.prompt_refs.flatMap(ref => (refMap.has(ref) ? [refMap.get(ref)!] : [])),
      ...(g.subtopics !== undefined
        ? {
            subtopics: g.subtopics.map(child => ({
              name: child.name,
              prompt_refs: child.prompt_refs.flatMap(ref =>
                refMap.has(ref) && g.prompt_refs.includes(ref) ? [refMap.get(ref)!] : []
              ),
            })),
          }
        : {}),
    }))
    .filter(g => g.prompt_refs.length);
  return groups.length
    ? { prompts: rows.map(({ text, ref }) => ({ text, ref })), prompt_groups: groups }
    : { prompts: rows.map(p => p.text) };
}
